import { Text } from '@/components/ui/text';
import { useEffect, useState, type FC } from 'react';

interface FdaRecallInputsProps {
  initialQuery: string;
  onQueryChange: (query: string, isValid: boolean) => void;
  showErrors: boolean;
  setShowErrors: (showErrors: boolean) => void;
}

export const FdaRecallInputs: FC<FdaRecallInputsProps> = ({
  initialQuery,
  onQueryChange,
  showErrors,
  setShowErrors,
}) => {
  const [productCode, setProductCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [recallingFirm, setRecallingFirm] = useState('');
  const [deviceName, setDeviceName] = useState('');

  const errorMessage =
    'At least one search criterion is required (product code, date, recalling firm, or device name)';

  // Parse initial query
  useEffect(() => {
    if (initialQuery) {
      const parts = initialQuery.split('|').map(p => p.trim());
      setProductCode(parts[0] || '');
      if (parts[1]) {
        const dates = parts[1].split('-');
        // Convert YYYYMMDD to YYYY-MM-DD for date inputs
        const formatToDateInput = (dateStr: string) => {
          if (!dateStr || dateStr.length !== 8) return '';
          return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
        };
        setStartDate(formatToDateInput(dates[0] || ''));
        setEndDate(formatToDateInput(dates[1] || ''));
      }
      if (parts[2]) {
        setRecallingFirm(parts[2]);
      }
      if (parts[3]) {
        setDeviceName(parts[3]);
      }
    }
  }, [initialQuery]);

  // Build and notify query changes
  useEffect(() => {
    const buildQuery = () => {
      // Convert YYYY-MM-DD to YYYYMMDD for backend
      const formatToBackend = (dateStr: string) => {
        if (!dateStr) return '';
        return dateStr.replace(/-/g, '');
      };

      let query = productCode.trim();
      const formattedStartDate = formatToBackend(startDate.trim());
      const formattedEndDate = formatToBackend(endDate.trim());

      // Add date range (always include as part 2, even if empty)
      query += ` | ${formattedStartDate}-${formattedEndDate}`;

      // Add recalling firm (part 3)
      query += ` | ${recallingFirm.trim()}`;

      // Add device name (part 4)
      query += ` | ${deviceName.trim()}`;

      return query;
    };

    const query = buildQuery();
    // Query is valid if at least one field has a value
    const isValid =
      productCode.trim().length > 0 ||
      startDate.trim().length > 0 ||
      endDate.trim().length > 0 ||
      recallingFirm.trim().length > 0 ||
      deviceName.trim().length > 0;
    onQueryChange(query, isValid);
    if (isValid) {
      setShowErrors(false);
    }
  }, [
    productCode,
    startDate,
    endDate,
    recallingFirm,
    deviceName,
    onQueryChange,
  ]);

  return (
    <>
      {/* Product Code Input */}
      <div className="flex flex-col gap-1 w-full">
        <Text variant="body-sm" className="font-medium text-neutral-600">
          Product Code (optional)
        </Text>
        <input
          type="text"
          placeholder="e.g., FOZ, NOF, or LWG"
          value={productCode}
          onChange={e => setProductCode(e.target.value.toUpperCase())}
          className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
        {showErrors && (
          <Text variant="body-xs" color="danger">
            {errorMessage}
          </Text>
        )}
      </div>

      {/* Date Range Inputs */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <Text variant="body-sm" className="font-medium text-neutral-600">
            Start Date (optional)
          </Text>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          />
          {showErrors && (
            <Text variant="body-xs" color="danger">
              {errorMessage}
            </Text>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <Text variant="body-sm" className="font-medium text-neutral-600">
            End Date (optional)
          </Text>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          />
          {showErrors && (
            <Text variant="body-xs" color="danger">
              {errorMessage}
            </Text>
          )}
        </div>
      </div>

      {/* Recalling Firm Input */}
      <div className="flex flex-col gap-1 w-full">
        <Text variant="body-sm" className="font-medium text-neutral-600">
          Recalling Firm (optional)
        </Text>
        <input
          type="text"
          placeholder="e.g., Medtronic"
          value={recallingFirm}
          onChange={e => setRecallingFirm(e.target.value)}
          className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
        {showErrors && (
          <Text variant="body-xs" color="danger">
            {errorMessage}
          </Text>
        )}
      </div>

      {/* Device Name Input */}
      <div className="flex flex-col gap-1 w-full">
        <Text variant="body-sm" className="font-medium text-neutral-600">
          Device Name (optional)
        </Text>
        <input
          type="text"
          placeholder="e.g., catheter, stent, or pacemaker"
          value={deviceName}
          onChange={e => setDeviceName(e.target.value)}
          className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
        {showErrors && (
          <Text variant="body-xs" color="danger">
            {errorMessage}
          </Text>
        )}
      </div>
    </>
  );
};
